/**
 * ASX nugget & alluvial signals — recent nugget finds, metal-detecting
 * evidence and historic alluvial ground that companies reported in ASX
 * announcements (goldfinder-llm-kb au-pathfinder-v1/docs/23), one point per
 * located find or mention, placed by the method it carries (L1 printed
 * coordinates … L4 a placed figure) with an error radius.
 *
 * ADMIN ONLY. ASX announcements are company copyright: the layer carries
 * derived facts, the short verified evidence quote and a link to the
 * company's own PDF. GeoServer layer `ne:asx_alluvial_signal`, read rule
 * ADMIN,GF_LAYERS_READ, so a deployed build reaches it only through the API's
 * admin route (gold-finder-api routes/layers.js) with a session cookie.
 *
 * Served as vector tiles from WMS GetMap (as the OCR gold values are). The
 * layer is under a thousand points, so tiles are not thinned and every class
 * and recency filter is applied here, client-side, with no refetch.
 */
export const ALLUVIAL_SIGNAL_WMS = process.env.REACT_APP_ALLUVIAL_SIGNAL_WMS
  || (process.env.NODE_ENV === 'development' ? 'http://localhost:8600/geoserver/ne/wms' : null);

export const ALLUVIAL_SIGNAL_AVAILABLE = !!ALLUVIAL_SIGNAL_WMS;
/** Served by the local GeoServer (a dev server), not a deployed one. */
export const ALLUVIAL_SIGNAL_LOCAL = !!ALLUVIAL_SIGNAL_WMS
  && /\/\/(localhost|127\.0\.0\.1)[:/]/.test(ALLUVIAL_SIGNAL_WMS);
/** Through the API's admin-only layers route: wait for useAdminSession. */
export const ALLUVIAL_SIGNAL_RESTRICTED = !!ALLUVIAL_SIGNAL_WMS
  && /\/service\/api\/layers\//.test(ALLUVIAL_SIGNAL_WMS);

export const ALLUVIAL_SIGNAL_LAYER = 'ne:asx_alluvial_signal';
/** MVT source-layer name: the GeoServer layer name without the workspace. */
export const ALLUVIAL_SIGNAL_SOURCE_LAYER = 'asx_alluvial_signal';
export const ALLUVIAL_SIGNAL_VECTOR_SRC = 'asx-alluvial';

/** Whole-of-WA from here: the full layer is a few hundred KB. */
export const ALLUVIAL_SIGNAL_MINZOOM = 4;
/** Deepest tile requested; MapLibre over-zooms past it. */
const VECTOR_MAXZOOM = 12;
/** The error-radius circles appear from this zoom. */
export const ALLUVIAL_SIGNAL_ERROR_MINZOOM = 9;

export function buildAlluvialSignalSource(wms = ALLUVIAL_SIGNAL_WMS) {
  // `styles=point` - the plain built-in style, so every feature reaches the
  // tile whatever the layer's own SLD rules say (see ocrGoldStyle.js).
  return {
    type: 'vector',
    tiles: [`${wms}?service=WMS&version=1.1.1&request=GetMap`
      + `&layers=${encodeURIComponent(ALLUVIAL_SIGNAL_LAYER)}&styles=point`
      + `&format=${encodeURIComponent('application/vnd.mapbox-vector-tile')}`
      + '&srs=EPSG:3857&bbox={bbox-epsg-3857}&width=512&height=512'],
    tileSize: 512,
    minzoom: ALLUVIAL_SIGNAL_MINZOOM,
    maxzoom: VECTOR_MAXZOOM,
  };
}

/**
 * The four classes (docs/23 §4 labels), in precedence order. One sidebar
 * checkbox each, held in overlayVisibility as booleans so presets carry them;
 * an absent key - a preset saved before these existed - means shown.
 */
export const ALLUVIAL_SIGNAL_CLASSES = [
  // find_new / find_repeat split docs/23 §12's class 'find' on first_release_held:
  // a repeat is a find we only hold recaps of, so its date is "at least this old".
  { value: 'find_new', label: 'New find', key: 'asxAlluvialNew', colour: '#d9480f' },
  { value: 'find_repeat', label: 'Repeat mention', key: 'asxAlluvialRepeat', colour: '#f59f00' },
  { value: 'detecting_evidence', label: 'Detecting evidence', key: 'asxAlluvialDetecting', colour: '#1971c2' },
  { value: 'historic_alluvial', label: 'Historic alluvial', key: 'asxAlluvialHistoric', colour: '#7d5a44' },
];
export const ALLUVIAL_CLASS_BY_VALUE = Object.fromEntries(ALLUVIAL_SIGNAL_CLASSES.map((c) => [c.value, c]));

export const alluvialClassesShown = (overlayVisibility) =>
  ALLUVIAL_SIGNAL_CLASSES.filter((c) => overlayVisibility?.[c.key] !== false).map((c) => c.value);

/**
 * Recency window, on recency_date (first release, or the latest release
 * reporting new recovery at the site). overlayVisibility holds only
 * booleans (presets and the overlay telemetry diff rely on it), so the choice
 * is one boolean per window, at most one true; none true means all.
 */
export const ALLUVIAL_RECENCY = [
  { value: '6', label: '6 mo', months: 6, key: 'asxAlluvialWithin6' },
  { value: '12', label: '12 mo', months: 12, key: 'asxAlluvialWithin12' },
  { value: '24', label: '24 mo', months: 24, key: 'asxAlluvialWithin24' },
  { value: 'all', label: 'All', months: null, key: null },
];
export const alluvialRecency = (overlayVisibility) =>
  ALLUVIAL_RECENCY.find((r) => r.key && overlayVisibility?.[r.key] === true)?.value || 'all';

/** The overlayVisibility keys to merge in to choose a recency window. */
export const alluvialRecencyPatch = (value) => Object.fromEntries(
  ALLUVIAL_RECENCY.filter((r) => r.key).map((r) => [r.key, r.value === value]),
);

/** ISO date (YYYY-MM-DD) `months` before `now`, in local time. */
export function monthsAgoIso(months, now = new Date()) {
  const d = new Date(now.getFullYear(), now.getMonth() - months, now.getDate());
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Recency date: coalesce(last new recovery, first release) - docs/23 §12's
 * layer contract (a recap never refreshes it; latest_mention is never used).
 * Arrives in a tile as 'YYYY-MM-DD' text, which sorts as a date.
 */
const RECENCY_DATE = ['to-string', ['coalesce', ['get', 'recency_date'], ['get', 'first_reported'], '']];

/** Earliest recency_date the recency choice keeps (ISO date), or null for all. */
export function alluvialRecencyCutoff(recency, now = new Date()) {
  const r = ALLUVIAL_RECENCY.find((x) => x.value === recency);
  return r && r.months ? monthsAgoIso(r.months, now) : null;
}

/** The MapLibre filter for the chosen classes and recency. */
export function alluvialSignalFilter(classes, recency, now = new Date()) {
  const parts = ['all', ['in', ['get', 'class'], ['literal', classes]]];
  const cutoff = alluvialRecencyCutoff(recency, now);
  if (cutoff) parts.push(['>=', RECENCY_DATE, cutoff]);
  return parts;
}

/**
 * Symbol by recency (recency_date): within 6 months a large point with a
 * dark edge, within 24 months a medium one, older a small faded one. Built
 * against today's date when the layer is made.
 */
export const ALLUVIAL_RECENCY_BANDS = [
  { label: '≤ 6 mo', months: 6, scale: 1.5, opacity: 1, stroke: '#1a1a1a' },
  { label: '≤ 24 mo', months: 24, scale: 1.15, opacity: 0.95, stroke: '#ffffff' },
  { label: 'older', months: null, scale: 0.85, opacity: 0.6, stroke: '#ffffff' },
];

const classColour = ['match', ['get', 'class'],
  ...ALLUVIAL_SIGNAL_CLASSES.flatMap((c) => [c.value, c.colour]), '#868e96'];

/** case-expression picking a recency band's property by recency_date. */
function byRecency(prop, now) {
  const [b6, b24, old] = ALLUVIAL_RECENCY_BANDS;
  return ['case',
    ['>=', RECENCY_DATE, monthsAgoIso(b6.months, now)], b6[prop],
    ['>=', RECENCY_DATE, monthsAgoIso(b24.months, now)], b24[prop],
    old[prop]];
}

/**
 * Metres -> screen pixels for a circle radius. MapLibre has no metre unit for
 * circles and an expression cannot read a point's latitude, so this uses
 * cos(29°S), mid-WA: within ±6% across the state (18°S-33°S). The world is
 * 512 px wide at zoom 0.
 */
const EARTH_CIRCUMFERENCE_M = 40075016.686;
const COS_LAT = Math.cos((29 * Math.PI) / 180);
export const metresPerPixel = (zoom) => (EARTH_CIRCUMFERENCE_M * COS_LAT) / (512 * 2 ** zoom);
const ERR_M = ['to-number', ['coalesce', ['get', 'err_m'], 0], 0];
const errRadiusPx = (zoom) => ['/', ERR_M, metresPerPixel(zoom)];

export const ALLUVIAL_SIGNAL_LAYER_ID = 'asx-alluvial-points';
export const ALLUVIAL_SIGNAL_ERROR_LAYER_ID = 'asx-alluvial-error';

/** The two map layers: the error-radius ring underneath, the points on top. */
export function alluvialSignalLayers(classes, recency, now = new Date()) {
  const filter = alluvialSignalFilter(classes, recency, now);
  const base = { source: ALLUVIAL_SIGNAL_VECTOR_SRC, 'source-layer': ALLUVIAL_SIGNAL_SOURCE_LAYER, filter };
  const scale = byRecency('scale', now);
  return [
    {
      ...base,
      id: ALLUVIAL_SIGNAL_ERROR_LAYER_ID,
      type: 'circle',
      minzoom: ALLUVIAL_SIGNAL_ERROR_MINZOOM,
      paint: {
        'circle-color': classColour,
        'circle-opacity': 0.06,
        'circle-stroke-color': classColour,
        'circle-stroke-width': 1,
        'circle-stroke-opacity': 0.55,
        'circle-pitch-alignment': 'map',
        'circle-radius': ['interpolate', ['exponential', 2], ['zoom'],
          ALLUVIAL_SIGNAL_ERROR_MINZOOM, errRadiusPx(ALLUVIAL_SIGNAL_ERROR_MINZOOM),
          22, errRadiusPx(22)],
      },
    },
    {
      ...base,
      id: ALLUVIAL_SIGNAL_LAYER_ID,
      type: 'circle',
      // Newest on top; new finds above repeats above evidence above history.
      layout: {
        'circle-sort-key': ['+',
          ['match', ['get', 'class'], 'find_new', 3000000, 'find_repeat', 2000000, 'detecting_evidence', 1000000, 0],
          // YYYYMM of recency_date (< 1,000,000, so it never outranks a class)
          ['to-number', ['concat', ['slice', RECENCY_DATE, 0, 4], ['slice', RECENCY_DATE, 5, 7]], 0]],
      },
      paint: {
        'circle-color': classColour,
        'circle-opacity': byRecency('opacity', now),
        'circle-stroke-color': byRecency('stroke', now),
        'circle-stroke-width': 1.3,
        'circle-radius': ['interpolate', ['linear'], ['zoom'],
          4, ['*', 3, scale], 9, ['*', 5, scale], 14, ['*', 7, scale]],
      },
    },
  ];
}

/** For click testing, and for useTopLayers. */
export const ALLUVIAL_SIGNAL_INTERACTIVE = [ALLUVIAL_SIGNAL_LAYER_ID];
export const ALLUVIAL_SIGNAL_ORDER = [ALLUVIAL_SIGNAL_ERROR_LAYER_ID, ALLUVIAL_SIGNAL_LAYER_ID];

/** Location rungs (docs/23 §5), for the popup. */
export const LOC_METHOD_LABEL = {
  L1: 'coordinates printed with the find',
  L2: "the find's own tenements",
  L2g: "company's own place-name gazetteer",
  L3: 'named MINEDEX gold site',
  L3c: 'named mining centre (MINEDEX cluster)',
  L4: 'placed map figure',
  L5: "announcement's tenements (area only)",
};

/** "500 m", "2.3 km", "18 km". */
export function fmtErr(errM) {
  const n = Number(errM);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 1000) return `${Math.round(n / 10) * 10} m`;
  if (n < 10000) return `${(n / 1000).toFixed(1)} km`;
  return `${Math.round(n / 1000)} km`;
}

/** Rows at one click: tile-edge duplicates removed, best class then newest first. */
export function rankPicked(propsList) {
  const seen = new Map();
  propsList.forEach((p) => { if (p && !seen.has(p.sig_id)) seen.set(p.sig_id, p); });
  const order = Object.fromEntries(ALLUVIAL_SIGNAL_CLASSES.map((c, i) => [c.value, i]));
  return [...seen.values()].sort((a, b) =>
    ((order[a.class] ?? 9) - (order[b.class] ?? 9))
    || String(b.recency_date || '').localeCompare(String(a.recency_date || '')));
}
